import { t, tf } from "./i18n.js";
// ---------------------------------------------------------------------------
// Caller: Sprachansage der Wuerfe (14.08.2026)
//
// WAS ER SAGT - UND WANN (korrigiert 15.08.2026 nach Alphatest)
// Wie ein Schiedsrichter, in zwei Momenten:
//
//   NACH der Aufnahme   den geworfenen SCORE ("Sixty")
//   VOR  der Aufnahme   den Verbleib des naechsten Werfers, ab Rest 170
//                       abwaerts ("You require 40")
//
// Beides steckt in EINER Ansage, weil die zweite unmittelbar auf die
// erste folgt und der Ton dann nahtlos geplant werden kann - zwei
// getrennte Ansagen wuerden sich gegenseitig abschneiden.
//
// VORHER war es ein ENTWEDER-ODER: im Finish-Bereich verschluckte der
// Caller den Score und sagte stattdessen den Rest DESSEN, der eben
// geworfen hatte - also die Zahl des Falschen zum falschen Zeitpunkt.
// Wessen Zahl der Verbleib ist, beantwortet game-501.js
// (naechsterAnwurf501) und liefert sie als `anwurf` mit.
//
// Dazu "Bust", "Game shot" und "Match".
//
// WAS DARAUS FOLGT: Score hoechstens 180, Rest hoechstens 170 - es wird
// NIE eine Zahl ueber 180 angesagt. Wer 0 bis 180 einspricht, braucht
// keine Hunderter-Bausteine und nie eine Zusammensetzung.
//
// KEIN CHECKOUT-VORSCHLAG (Entscheidung 14.08.2026)
// Ein Schiedsrichter nennt den Rest, nie den Weg. Der fruehere Zusatz
// "Doppel 20" ist deshalb ersatzlos entfallen - samt der Dateien
// double/treble/d20 und der Einstellung dazu.
//
// EIGENE AUFNAHMEN STATT MASCHINENSTIMME (Umbau 14.08.2026)
// Die erste Fassung sprach ueber die Web Speech API. Das war beim Bauen
// bequem und klingt im Betrieb schlecht - eine Roboterstimme nimmt einem
// Spiel die Spannung, statt sie zu machen. Jetzt gilt: EIGENE
// AUFNAHMEN sind der Normalfall, die Maschinenstimme nur noch der
// Rueckfall (abschaltbar).
//
// DER ENTSCHEIDENDE PUNKT: WIE WENIG MAN EINSPRECHEN MUSS
// Jede Ansage wird zuerst in BAUSTEINE zerlegt (Token), und fuer jeden
// Baustein sucht der Caller die BESTE vorhandene Datei:
//
//   1. die genaue Datei    180.mp3          <- klingt am besten
//   2. sonst zusammengesetzt   100.mp3 + 80.mp3
//   3. sonst (ganze Ansage) die Maschinenstimme, falls erlaubt
//
// Damit reichen 0-99 plus die Hunderter (100, 200 ... 500), um JEDE
// Zahl von 0 bis 599 zu sagen - also jeden Score (0-180) und jeden Rest
// (2-501). Wer die grossen Scores zusaetzlich am Stueck einspricht
// ("Einhundertachtzig!"), bekommt sie automatisch bevorzugt: Die genaue
// Datei gewinnt immer. Man kann also mit einer Handvoll Dateien
// anfangen und den Satz jederzeit erweitern - fehlende Dateien fallen
// still zurueck, nichts geht kaputt.
//
// WARUM WEB AUDIO UND KEIN <audio>-ELEMENT
// Zusammengesetzte Zahlen ("einhundert" + "achtzig") duerfen keine
// Luecke haben. HTMLAudioElement startet mit unvorhersehbarer
// Verzoegerung; die Web Audio API plant Puffer auf die Millisekunde
// genau hintereinander. Nur so klingt die Zusammensetzung wie ein Wort.
//
// WOHER DIE EREIGNISSE KOMMEN
// game-501.js meldet jeden GEWERTETEN Wurf per CustomEvent
// "secure-darts:wurf". Das ist bewusst ein Ereignis und keine
// DOM-Beobachtung: Wirft jemand zweimal hintereinander dieselbe Zahl
// (26, 26 - im Darts alltaeglich), aendert sich der Text im Anzeigefeld
// NICHT, und ein MutationObserver bliebe stumm.
//
// Den MATCH-Gewinn liest der Caller dagegen aus der Siegeranzeige
// (#win-subtitle-501, MutationObserver) - dort ist der Text je Ereignis
// verschieden, ein Beobachter also verlaesslich, und die Spiellogik
// bleibt unberuehrt (Muster wie spiegelRest in board-input.js).
//
// ZUSICHERUNG
// Der Caller ist Beiwerk: Jede Ausnahme wird gefangen, keine Ansage
// darf je einen Wurf kosten. Er liest Zustand, er veraendert keinen.
// ---------------------------------------------------------------------------

const CALLER_KEY = "secure-darts:caller";

// Wo die Aufnahmen liegen. frontend/public/ wird von Vite unveraendert
// in die Auslieferung kopiert - aus public/caller/standard/180.mp3 wird
// also /caller/standard/180.mp3. Ordner anlegen, Dateien hineinlegen,
// fertig; es gibt keine Liste zu pflegen.
const AUDIO_BASIS = "/caller";

// Dateiendungen in Suchreihenfolge. Sobald eine geklappt hat, wird sie
// zuerst probiert - wer alles als .wav einspricht, erzeugt dadurch nur
// einen einzigen Fehlversuch statt einen je Datei.
const ENDUNGEN = ["mp3", "wav", "ogg", "m4a"];

// Ab diesem Rest sagt der Caller den Verbleib an ("you require ...").
// 170 ist die hoechste mit drei Darts ausmachbare Zahl - genau die
// Grenze, ab der ein Schiedsrichter mitzaehlt.
const FINISH_AB = 170;

// Feste Begriffe. `dateien` sind die Dateinamen in Suchreihenfolge -
// englische zuerst, weil die klassische Caller-Sprache Englisch ist; ein
// deutsches Paket wird trotzdem gefunden. Der gesprochene Text kommt
// aus i18n.js und folgt damit der Spracheinstellung; er dient NUR dem
// Rueckfall auf die Systemstimme (eigene Aufnahmen bringen ihre Sprache
// selbst mit).
const WORTE = {
  bust: { dateien: ["bust"] },
  gameShot: { dateien: ["game-shot"] },
  match: { dateien: ["match"] },
  youRequire: { dateien: ["you-require"] },
};

// Rueckfalltexte, falls i18n.js nicht geladen ist (Tests, Teilseiten).
const WORT_VORGABE = {
  bust: "Bust",
  gameShot: "Game shot",
  match: "Match",
  youRequire: "You require",
};

function wortText(schluessel) {
  const uebersetzt =
    typeof window !== "undefined" && typeof window.t === "function"
      ? window.t(`caller.${schluessel}`, "")
      : "";
  return uebersetzt || WORT_VORGABE[schluessel] || "";
}

const callerZustand = {
  entsperrt: false,
  stimmen: [],
  letzterSieg: "",
  ctx: null, // AudioContext, erst bei der ersten Ansage
  gain: null,
  puffer: new Map(), // Dateiname (ohne Endung) -> AudioBuffer | null (= fehlt)
  laufend: [], // aktive AudioBufferSourceNodes, fuer sauberes Abbrechen
  endung: null, // die Endung, die zuletzt funktioniert hat
};

// ── Einstellungen ──────────────────────────────────────────────────────────

function callerEinstellungen() {
  const vorgabe = {
    enabled: false,
    paket: "standard", // Unterordner in public/caller/
    rueckfallStimme: true, // fehlt eine Aufnahme: Maschinenstimme?
    stimme: "", // voiceURI des Rueckfalls
    lautstaerke: 1.0,
    tempo: 1.0,
    punkte: true,
    rest: true,
    bust: true,
    spielende: true,
  };
  try {
    const roh = localStorage.getItem(CALLER_KEY);
    return roh ? { ...vorgabe, ...JSON.parse(roh) } : vorgabe;
  } catch (error) {
    return vorgabe;
  }
}

function speichereCallerEinstellungen(teil) {
  const neu = { ...callerEinstellungen(), ...teil };
  try {
    localStorage.setItem(CALLER_KEY, JSON.stringify(neu));
  } catch (error) {
    // Speicher gesperrt: Einstellung gilt bis zum Neuladen.
  }
  // Ein Paketwechsel macht den Pufferspeicher wertlos - sonst spraeche
  // die alte Stimme weiter, bis jemand neu laedt.
  if (teil.paket !== undefined) callerZustand.puffer.clear();
  if (callerZustand.gain) {
    callerZustand.gain.gain.value = Math.min(
      1,
      Math.max(0, Number(neu.lautstaerke) || 1),
    );
  }
  return neu;
}

// ── Bausteine einer Ansage (rein, deshalb testbar) ─────────────────────────
//
// Ein Token ist { typ: "wort" | "zahl", wert: ... }. Aus
// derselben Liste entstehen BEIDE Ausgaben: die Dateifolge fuer die
// eigenen Aufnahmen und der Satz fuer die Maschinenstimme. Zwei
// getrennte Zusammenstellungen waeren die naechste Stelle, an der beide
// auseinanderlaufen.

/** Die Bausteine einer Wurfansage - oder [] fuer Schweigen.
 *
 * Reihenfolge wie beim Schiedsrichter: erst der Score, den die eben
 * beendete Aufnahme gebracht hat, dann der Verbleib fuer den, der JETZT
 * an die Oche tritt ("You require ..."). Beides in einer Ansage, weil es
 * beim Schiedsrichter auch ein Atemzug ist.
 *
 * Der Verbleib kommt aus `ereignis.anwurf` (game-501.js,
 * naechsterAnwurf501) - NICHT aus `ereignis.rest`. `rest` ist der Rest
 * des Werfers, der gerade fertig ist; ihn anzusagen hiesse, dem
 * naechsten Spieler eine fremde Zahl mitzugeben. */
function bausteineFuerWurf(ereignis, conf) {
  const teile = [];
  const score = Number(ereignis?.score);

  if (ereignis?.bust) {
    // Der Bust wird IMMER genannt - er erklaert, warum der Rest steht.
    if (conf.bust) teile.push({ typ: "wort", wert: "bust" });
  } else if (ereignis?.finish) {
    if (conf.spielende) teile.push({ typ: "wort", wert: "gameShot" });
  } else if (conf.punkte) {
    // Auch die Null ist eine Zahl-Datei: "0" spricht das Paket als
    // "No score" (englisch) oder "Kein Punkt" (deutsch) - eine Datei
    // weniger, die jemand vergessen kann.
    teile.push({ typ: "zahl", wert: score });
  }

  // Nach einem Finish tritt niemand mehr an (anwurf ist dann null).
  const anwurf = Number(ereignis?.anwurf?.rest);
  if (
    conf.rest &&
    !ereignis?.finish &&
    Number.isFinite(anwurf) &&
    anwurf > 0 &&
    anwurf <= FINISH_AB
  ) {
    // verbinde: "You require" und die Zahl sind EINE Wendung, nicht zwei
    // Ansagen. Fuer die Aufnahmen ohne Belang - dort laufen die Dateien
    // ohnehin nahtlos -, fuer die Systemstimme der Unterschied zwischen
    // Satz und Aufzaehlung.
    teile.push(
      { typ: "wort", wert: "youRequire", verbinde: true },
      { typ: "zahl", wert: anwurf },
    );
  }
  return teile;
}

/** Bausteine -> gesprochener Satz (Rueckfall Maschinenstimme).
 *
 * Komma zwischen den Ansagen: Die Stimme macht dadurch eine kurze
 * Pause, statt alles zu einem Wort zu verschleifen. Bausteine mit
 * `verbinde` gehoeren dagegen zusammen ("Rest 40"). */
function tokenZuText(tokens) {
  const wortFuer = (t) => {
    if (t.typ === "wort") return wortText(t.wert);
    if (t.typ === "zahl") return String(t.wert);
    return "";
  };
  const teile = [];
  let offen = "";
  for (const token of tokens) {
    const wort = wortFuer(token);
    if (!wort) continue;
    if (offen) {
      teile.push(`${offen} ${wort}`);
      offen = "";
    } else if (token.verbinde) {
      offen = wort;
    } else {
      teile.push(wort);
    }
  }
  if (offen) teile.push(offen);
  return teile.join(", ");
}

/** Bequemer Durchgriff - eine Ansage als Text. Bleibt erhalten, weil
 *  Tests und Fehlersuche damit arbeiten. */
function ansageFuerWurf(ereignis, conf) {
  return tokenZuText(bausteineFuerWurf(ereignis, conf));
}

// ── Welche Dateien braucht ein Baustein? ───────────────────────────────────

/** Zahl in Dateinamen. 0-99 ist immer eine Datei; darueber Hunderter
 *  plus Rest ("einhundert" + "achtzig"). Deutsch faellt genau an dieser
 *  Naht auseinander, deshalb hoechstens EIN Schnitt je Zahl. */
function zahlZerlegung(n) {
  if (!Number.isFinite(n) || n < 0 || n > 999) return null;
  if (n <= 99) return [String(n)];
  const hunderter = Math.floor(n / 100) * 100;
  const rest = n % 100;
  return rest === 0 ? [String(hunderter)] : [String(hunderter), String(rest)];
}

/** Moegliche Dateifolgen fuer einen Baustein, beste zuerst.
 *  Der Aufrufer nimmt die erste, die VOLLSTAENDIG vorhanden ist. */
function dateiKandidaten(token) {
  if (token.typ === "wort") {
    // Mehrere Schreibweisen je Begriff sind moeglich; die erste
    // vorhandene gewinnt.
    return (WORTE[token.wert]?.dateien || []).map((datei) => [datei]);
  }
  if (token.typ === "zahl") {
    const kandidaten = [];
    const genau = String(token.wert);
    kandidaten.push([genau]);
    const zerlegt = zahlZerlegung(token.wert);
    if (zerlegt && zerlegt.join() !== genau) kandidaten.push(zerlegt);
    return kandidaten;
  }
  return [];
}

// ── Aufnahmen laden und abspielen ──────────────────────────────────────────

function audioKontext() {
  if (callerZustand.ctx) return callerZustand.ctx;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  const ctx = new Ctx();
  const gain = ctx.createGain();
  gain.gain.value = Math.min(
    1,
    Math.max(0, Number(callerEinstellungen().lautstaerke) || 1),
  );
  gain.connect(ctx.destination);
  callerZustand.ctx = ctx;
  callerZustand.gain = gain;
  return ctx;
}

function dateiPfad(name, endung) {
  const conf = callerEinstellungen();
  const paket = encodeURIComponent(conf.paket || "standard");
  return `${AUDIO_BASIS}/${paket}/${encodeURIComponent(name)}.${endung}`;
}

/** Laedt eine Datei und legt sie ab. null = gibt es nicht (auch das wird
 *  gemerkt, damit nicht bei jedem Wurf erneut gesucht wird). */
async function ladePuffer(name) {
  if (callerZustand.puffer.has(name)) return callerZustand.puffer.get(name);
  const ctx = audioKontext();
  if (!ctx) return null;

  // Zuletzt erfolgreiche Endung zuerst - spart die Fehlversuche.
  const reihenfolge = callerZustand.endung
    ? [callerZustand.endung, ...ENDUNGEN.filter((e) => e !== callerZustand.endung)]
    : ENDUNGEN;

  for (const endung of reihenfolge) {
    try {
      const antwort = await fetch(dateiPfad(name, endung));
      if (!antwort.ok) continue;
      const roh = await antwort.arrayBuffer();
      const puffer = await ctx.decodeAudioData(roh);
      callerZustand.puffer.set(name, puffer);
      callerZustand.endung = endung;
      return puffer;
    } catch (error) {
      // Nicht da oder nicht dekodierbar: naechste Endung.
    }
  }
  callerZustand.puffer.set(name, null);
  return null;
}

/** Die erste vollstaendig vorhandene Dateifolge fuer einen Baustein. */
async function loeseTokenAuf(token) {
  for (const folge of dateiKandidaten(token)) {
    const puffer = await Promise.all(folge.map(ladePuffer));
    if (puffer.every(Boolean)) return puffer;
  }
  return null;
}

function stoppeLaufende() {
  for (const quelle of callerZustand.laufend) {
    try {
      quelle.stop();
    } catch (error) {
      /* lief schon nicht mehr */
    }
  }
  callerZustand.laufend = [];
}

/** Spielt die Bausteine als eigene Aufnahmen. false = mindestens ein
 *  Baustein fehlt; dann hat der Aufrufer nichts abgespielt und kann auf
 *  die Maschinenstimme ausweichen.
 *
 * ALLES ODER NICHTS: Fehlt eine Datei, wird die GANZE Ansage der
 * Maschinenstimme ueberlassen. Die eigene Stimme mitten im Satz gegen
 * eine synthetische zu tauschen klingt schlimmer als beides einzeln. */
async function spieleAufnahmen(tokens, { abbrechen = true } = {}) {
  const ctx = audioKontext();
  if (!ctx || !tokens.length) return false;

  const folgen = [];
  for (const token of tokens) {
    const puffer = await loeseTokenAuf(token);
    if (!puffer) return false;
    folgen.push(...puffer);
  }

  try {
    if (ctx.state === "suspended") await ctx.resume();
  } catch (error) {
    /* ohne Nutzergeste bleibt er stumm - beim naechsten Klick geht es */
  }
  if (abbrechen) stoppeLaufende();

  // Nahtlos hintereinander planen. Zwischen Bausteinen derselben Zahl
  // darf keine Luecke sein; zwischen Saetzen ist eine kurze gewollt -
  // das entscheidet die Aufnahme selbst (Stille am Dateiende).
  const tempo = Math.min(2, Math.max(0.5, Number(callerEinstellungen().tempo) || 1));
  let zeit = ctx.currentTime + 0.02;
  for (const puffer of folgen) {
    const quelle = ctx.createBufferSource();
    quelle.buffer = puffer;
    quelle.playbackRate.value = tempo;
    quelle.connect(callerZustand.gain);
    quelle.start(zeit);
    callerZustand.laufend.push(quelle);
    quelle.onended = () => {
      callerZustand.laufend = callerZustand.laufend.filter((q) => q !== quelle);
    };
    zeit += puffer.duration / tempo;
  }
  return true;
}

// ── Rueckfall: Maschinenstimme ─────────────────────────────────────────────

function ladeStimmen() {
  try {
    callerZustand.stimmen = window.speechSynthesis?.getVoices?.() || [];
  } catch (error) {
    callerZustand.stimmen = [];
  }
  return callerZustand.stimmen;
}

function stimmeFinden(conf) {
  const alle = callerZustand.stimmen.length
    ? callerZustand.stimmen
    : ladeStimmen();
  if (!alle.length) return null;
  if (conf.stimme) {
    const gewaehlt = alle.find((s) => s.voiceURI === conf.stimme);
    if (gewaehlt) return gewaehlt;
  }
  return alle.find((s) => (s.lang || "").toLowerCase().startsWith("de")) || null;
}

function sprichSynthetisch(text, { abbrechen = true } = {}) {
  if (!text) return;
  try {
    const synth = window.speechSynthesis;
    if (!synth) return;
    if (abbrechen) synth.cancel();
    const conf = callerEinstellungen();
    const satz = new SpeechSynthesisUtterance(text);
    const stimme = stimmeFinden(conf);
    if (stimme) satz.voice = stimme;
    satz.lang = stimme?.lang || "de-DE";
    satz.volume = Math.min(1, Math.max(0, Number(conf.lautstaerke) || 1));
    satz.rate = Math.min(2, Math.max(0.5, Number(conf.tempo) || 1));
    synth.speak(satz);
  } catch (error) {
    // Eine stumme Ansage ist ein Schoenheitsfehler, kein Spielfehler.
  }
}

/** Der eine Weg nach draussen: erst die eigenen Aufnahmen, sonst - wenn
 *  erlaubt - die Maschinenstimme. */
async function sage(tokens, optionen = {}) {
  const conf = callerEinstellungen();
  if (!conf.enabled || !tokens.length) return;
  try {
    const gespielt = await spieleAufnahmen(tokens, optionen);
    if (gespielt) return;
    if (conf.rueckfallStimme) sprichSynthetisch(tokenZuText(tokens), optionen);
  } catch (error) {
    /* siehe Zusicherung im Modulkopf */
  }
}

// Autoplay-Sperre: Browser lassen Ton erst nach einer Nutzergeste zu.
function entsperreTon() {
  if (callerZustand.entsperrt) return;
  callerZustand.entsperrt = true;
  try {
    audioKontext()?.resume?.();
  } catch (error) {
    /* dann eben beim naechsten Versuch */
  }
  try {
    const synth = window.speechSynthesis;
    if (!synth) return;
    synth.resume();
  } catch (error) {
    /* nichts weiter */
  }
}

// ── Ereignisse ─────────────────────────────────────────────────────────────

function aufWurf(ereignis) {
  try {
    const conf = callerEinstellungen();
    if (!conf.enabled) return;
    const detail = ereignis?.detail || {};
    const tokens = bausteineFuerWurf(detail, conf);
    // Ein Finish nicht abschneiden lassen - danach kommt evtl. der
    // Matchsieger aus dem Beobachter.
    sage(tokens, { abbrechen: !detail.finish });
  } catch (error) {
    /* siehe Zusicherung im Modulkopf */
  }
}

// Matchsieger aus der Siegeranzeige. Nur das MATCH - das Leg hat der
// Wurf selbst schon mit "Game shot" angesagt.
function beobachteSiegeranzeige() {
  const feld = document.getElementById("win-subtitle-501");
  if (!feld || feld.dataset.callerBeobachtet) return;
  feld.dataset.callerBeobachtet = "1";

  const pruefe = () => {
    try {
      const conf = callerEinstellungen();
      if (!conf.enabled || !conf.spielende) return;
      const text = (feld.textContent || "").trim();
      if (!text || text === callerZustand.letzterSieg) return;
      if (!/gewinnt das Match/i.test(text)) return;
      callerZustand.letzterSieg = text;
      // Der Name kommt aus dem Spiel und kann nicht eingesprochen sein -
      // deshalb nur "Match", der Name steht gross auf dem Schirm.
      sage([{ typ: "wort", wert: "match" }], { abbrechen: false });
    } catch (error) {
      /* Ansage ist Beiwerk */
    }
  };
  new MutationObserver(pruefe).observe(feld, {
    childList: true,
    characterData: true,
    subtree: true,
  });
}

// ── Einstellungsseite ──────────────────────────────────────────────────────

function fuelleCallerFormular() {
  const conf = callerEinstellungen();
  const setze = (id, wert) => {
    const el = document.getElementById(id);
    if (!el) return;
    if (el.type === "checkbox") el.checked = Boolean(wert);
    else el.value = wert;
  };
  setze("caller-enabled", conf.enabled);
  // Das Paketfeld ist seit 15.08.2026 eine Auswahlliste. Zeigt eine alte,
  // frei getippte Einstellung auf ein Paket, das es nicht (mehr) gibt,
  // bliebe die Liste sonst leer ausgewaehlt - und der Caller haette gar
  // kein Paket. Dann faellt sie auf den ersten Eintrag zurueck.
  const paketFeld = document.getElementById("caller-paket");
  if (paketFeld && paketFeld.tagName === "SELECT") {
    const bekannt = [...paketFeld.options].some((o) => o.value === conf.paket);
    paketFeld.value = bekannt ? conf.paket : paketFeld.options[0]?.value || "standard";
  } else {
    setze("caller-paket", conf.paket);
  }
  setze("caller-rueckfall", conf.rueckfallStimme);
  setze("caller-lautstaerke", conf.lautstaerke);
  setze("caller-tempo", conf.tempo);
  setze("caller-punkte", conf.punkte);
  setze("caller-rest", conf.rest);
  setze("caller-bust", conf.bust);
  setze("caller-spielende", conf.spielende);

  const auswahl = document.getElementById("caller-stimme");
  if (!auswahl) return;
  const zeichne = () => {
    const stimmen = ladeStimmen();
    const deutsche = stimmen.filter((s) =>
      (s.lang || "").toLowerCase().startsWith("de"),
    );
    const liste = deutsche.length ? deutsche : stimmen;
    auswahl.innerHTML =
      `<option value="">${t("js.caller.stimmeAuto")}</option>` +
      liste
        .map(
          (s) =>
            `<option value="${s.voiceURI}"${
              s.voiceURI === conf.stimme ? " selected" : ""
            }>${s.name} (${s.lang})</option>`,
        )
        .join("");
  };
  zeichne();
  if (window.speechSynthesis) window.speechSynthesis.onvoiceschanged = zeichne;
}

function speichereCallerFormular() {
  const el = (id) => document.getElementById(id);
  speichereCallerEinstellungen({
    enabled: Boolean(el("caller-enabled")?.checked),
    paket: (el("caller-paket")?.value || "standard").trim() || "standard",
    rueckfallStimme: Boolean(el("caller-rueckfall")?.checked),
    stimme: el("caller-stimme")?.value || "",
    lautstaerke: parseFloat(el("caller-lautstaerke")?.value) || 1,
    tempo: parseFloat(el("caller-tempo")?.value) || 1,
    punkte: Boolean(el("caller-punkte")?.checked),
    rest: Boolean(el("caller-rest")?.checked),
    bust: Boolean(el("caller-bust")?.checked),
    spielende: Boolean(el("caller-spielende")?.checked),
  });
  window.showToast?.(t("js.caller.001"), "success");
}

/** Probeansage: "Einhundertachtzig" - also genau der Fall, der eine
 *  Zusammensetzung braucht, wenn 180 nicht am Stueck vorliegt. */
function testeCallerStimme() {
  entsperreTon();
  const conf = callerEinstellungen();
  const merker = conf.enabled;
  if (!merker) speichereCallerEinstellungen({ enabled: true });
  Promise.resolve(sage([{ typ: "zahl", wert: 180 }])).finally(() => {
    if (!merker) speichereCallerEinstellungen({ enabled: false });
  });
}

// ── Start ──────────────────────────────────────────────────────────────────

if (typeof window !== "undefined") {
  ladeStimmen();
  if (window.speechSynthesis) window.speechSynthesis.onvoiceschanged = ladeStimmen;
  window.addEventListener("secure-darts:wurf", aufWurf);
  window.addEventListener("pointerdown", entsperreTon, { once: true });
  window.addEventListener("keydown", entsperreTon, { once: true });

  const start = () => beobachteSiegeranzeige();
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
  // Die Siegeranzeige kommt aus einem Partial und kann spaeter im DOM
  // auftauchen als dieses Modul - beim ersten Wurf noch einmal nachsehen.
  window.addEventListener("secure-darts:wurf", beobachteSiegeranzeige);
}

// ---------------------------------------------------------------
// Bruecke zu window: Die Einstellungsseite ruft die Formularfunktionen
// per Inline-Handler; caller.* dient der Fehlersuche in der Konsole.
// ---------------------------------------------------------------
const caller = {
  einstellungen: callerEinstellungen,
  speichern: speichereCallerEinstellungen,
  sage,
  bausteine: bausteineFuerWurf,
};

Object.assign(window, {
  caller,
  fuelleCallerFormular,
  speichereCallerFormular,
  testeCallerStimme,
});

export {
  ansageFuerWurf,
  bausteineFuerWurf,
  tokenZuText,
  dateiKandidaten,
  zahlZerlegung,
  callerEinstellungen,
  speichereCallerEinstellungen,
};
